import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  ShoppingCart, 
  Euro,
  Calendar,
  TrendingUp,
  TrendingDown,
  FileSpreadsheet,
  FileText,
  Eye,
  Filter,
  X
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SoldBicycles() {
  const { isAdmin } = useAuth();
  const [bicycles, setBicycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchSoldBicycles();
  }, []);

  const fetchSoldBicycles = async () => {
    try {
      const response = await axios.get(`${API}/bicycles/sold/list`);
      setBicycles(response.data);
    } catch (error) {
      toast.error('Error al cargar las bicicletas vendidas');
    } finally {
      setLoading(false);
    }
  };

  // Filter bicycles by date range
  const filteredBicycles = useMemo(() => {
    return bicycles.filter(bike => {
      if (!bike.sold_date) return false;
      const soldDate = new Date(bike.sold_date);
      
      if (dateFrom) {
        const from = new Date(dateFrom);
        if (soldDate < from) return false;
      }
      
      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 999);
        if (soldDate > to) return false;
      }
      
      return true;
    });
  }, [bicycles, dateFrom, dateTo]);

  // Calculate stats from filtered data
  const stats = useMemo(() => {
    const totalSold = filteredBicycles.length;
    const totalProfit = filteredBicycles.reduce((sum, b) => sum + (b.profit || 0), 0);
    const totalRevenue = filteredBicycles.reduce((sum, b) => sum + (b.sold_price || 0), 0);
    const totalRentalIncome = filteredBicycles.reduce((sum, b) => sum + (b.rental_income || 0), 0);
    
    return { totalSold, totalProfit, totalRevenue, totalRentalIncome };
  }, [filteredBicycles]);

  // Monthly sales data for chart
  const monthlyData = useMemo(() => {
    const monthMap = {};
    
    filteredBicycles.forEach(bike => {
      if (!bike.sold_date) return;
      const date = new Date(bike.sold_date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthMap[monthKey]) {
        monthMap[monthKey] = {
          month: monthKey,
          ventas: 0,
          ingresos: 0,
          beneficio: 0,
          alquiler: 0
        };
      }
      
      monthMap[monthKey].ventas += 1;
      monthMap[monthKey].ingresos += (bike.sold_price || 0);
      monthMap[monthKey].beneficio += (bike.profit || 0);
      monthMap[monthKey].alquiler += (bike.rental_income || 0);
    });
    
    return Object.values(monthMap).sort((a, b) => a.month.localeCompare(b.month));
  }, [filteredBicycles]);

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatMonthLabel = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(30, 41, 59);
    doc.text('Informe de Ventas - VeloFlux Fleet', 14, 22);
    
    // Date range
    doc.setFontSize(10);
    doc.setTextColor(100);
    let dateRangeText = 'Período: ';
    if (dateFrom && dateTo) {
      dateRangeText += `${formatDate(dateFrom)} - ${formatDate(dateTo)}`;
    } else if (dateFrom) {
      dateRangeText += `Desde ${formatDate(dateFrom)}`;
    } else if (dateTo) {
      dateRangeText += `Hasta ${formatDate(dateTo)}`;
    } else {
      dateRangeText += 'Todos los registros';
    }
    doc.text(dateRangeText, 14, 30);
    doc.text(`Generado: ${new Date().toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}`, 14, 36);
    
    // Summary stats
    doc.setFontSize(12);
    doc.setTextColor(30, 41, 59);
    doc.text('Resumen', 14, 50);
    
    doc.setFontSize(10);
    doc.text(`Total Bicicletas Vendidas: ${stats.totalSold}`, 14, 58);
    doc.text(`Ingresos por Ventas: ${formatCurrency(stats.totalRevenue)}`, 14, 65);
    doc.text(`Ingresos por Alquiler: ${formatCurrency(stats.totalRentalIncome)}`, 14, 72);
    doc.text(`Beneficio Total: ${formatCurrency(stats.totalProfit)}`, 14, 79);
    
    // Table
    const tableData = filteredBicycles.map(bike => [
      bike.number,
      bike.model,
      bike.size,
      formatDate(bike.sold_date),
      formatCurrency(bike.purchase_price),
      formatCurrency(bike.total_maintenance_cost),
      formatCurrency(bike.sold_price),
      formatCurrency(bike.rental_income),
      formatCurrency(bike.profit)
    ]);
    
    autoTable(doc, {
      startY: 90,
      head: [['Nº', 'Modelo', 'Talla', 'F. Venta', 'P. Compra', 'Manten.', 'P. Venta', 'Alquiler', 'Beneficio']],
      body: tableData,
      theme: 'striped',
      headStyles: { 
        fillColor: [249, 115, 22],
        textColor: 255,
        fontStyle: 'bold'
      },
      styles: { 
        fontSize: 8,
        cellPadding: 2
      },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 30 },
        2: { cellWidth: 12 },
        3: { cellWidth: 20 },
        4: { cellWidth: 20 },
        5: { cellWidth: 18 },
        6: { cellWidth: 20 },
        7: { cellWidth: 18 },
        8: { cellWidth: 20 }
      }
    });
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Página ${i} de ${pageCount} - VeloFlux Fleet Manager`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }
    
    doc.save(`ventas-veloflux-${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF exportado correctamente');
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    // Summary sheet
    const summaryData = [
      ['INFORME DE VENTAS - VELOFLUX FLEET'],
      [''],
      ['Fecha de generación:', new Date().toLocaleDateString('es-ES')],
      ['Período:', dateFrom && dateTo ? `${dateFrom} - ${dateTo}` : 'Todos'],
      [''],
      ['RESUMEN'],
      ['Total Bicicletas Vendidas:', stats.totalSold],
      ['Ingresos por Ventas:', stats.totalRevenue],
      ['Ingresos por Alquiler:', stats.totalRentalIncome],
      ['Beneficio Total:', stats.totalProfit],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumen');
    
    // Detail sheet
    const detailData = filteredBicycles.map(bike => ({
      'Número': bike.number,
      'Modelo': bike.model,
      'Talla': bike.size,
      'Fecha Venta': formatDate(bike.sold_date),
      'Precio Compra': bike.purchase_price || 0,
      'Gastos Mantenimiento': bike.total_maintenance_cost || 0,
      'Precio Venta': bike.sold_price || 0,
      'Ingresos Alquiler': bike.rental_income || 0,
      'Beneficio': bike.profit || 0,
      'Nº Bastidor': bike.frame_number || '',
      'Nº Factura': bike.invoice_number || ''
    }));
    
    const detailSheet = XLSX.utils.json_to_sheet(detailData);
    XLSX.utils.book_append_sheet(workbook, detailSheet, 'Detalle Ventas');
    
    // Monthly summary sheet
    if (monthlyData.length > 0) {
      const monthlySheetData = monthlyData.map(m => ({
        'Mes': formatMonthLabel(m.month),
        'Bicis Vendidas': m.ventas,
        'Ingresos Ventas': m.ingresos,
        'Ingresos Alquiler': m.alquiler,
        'Beneficio': m.beneficio
      }));
      const monthlySheet = XLSX.utils.json_to_sheet(monthlySheetData);
      XLSX.utils.book_append_sheet(workbook, monthlySheet, 'Resumen Mensual');
    }
    
    XLSX.writeFile(workbook, `ventas-veloflux-${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel exportado correctamente');
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">Acceso denegado</p>
      </div>
    );
  }

  const hasFilters = dateFrom || dateTo;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="sold-bicycles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold font-['Barlow_Condensed'] tracking-tight text-slate-900">
            Bicicletas Vendidas
          </h1>
          <p className="text-slate-500 mt-1">Historial de ventas y análisis de beneficios</p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={exportToPDF}
            data-testid="export-pdf-btn"
          >
            <FileText size={16} />
            Exportar PDF
          </Button>
          <Button 
            className="bg-green-600 hover:bg-green-700 text-white gap-2"
            onClick={exportToExcel}
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet size={16} />
            Exportar Excel
          </Button>
        </div>
      </div>

      {/* Date Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex items-center gap-2 text-slate-600">
              <Filter size={18} />
              <span className="font-medium">Filtrar por fecha:</span>
            </div>
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Desde</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full"
                  data-testid="date-from-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Hasta</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full"
                  data-testid="date-to-input"
                />
              </div>
            </div>
            {hasFilters && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearFilters}
                className="text-slate-500 hover:text-slate-700 gap-1"
              >
                <X size={14} />
                Limpiar
              </Button>
            )}
          </div>
          {hasFilters && (
            <p className="text-sm text-orange-600 mt-2">
              Mostrando {filteredBicycles.length} de {bicycles.length} ventas
            </p>
          )}
        </CardContent>
      </Card>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <ShoppingCart size={18} className="text-green-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Total Vendidas</p>
                <p className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed']">
                  {stats.totalSold}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Euro size={18} className="text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Ingresos Ventas</p>
                <p className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed']">
                  {formatCurrency(stats.totalRevenue)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Calendar size={18} className="text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Ingresos Alquiler</p>
                <p className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed']">
                  {formatCurrency(stats.totalRentalIncome)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`border-2 ${stats.totalProfit >= 0 ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stats.totalProfit >= 0 ? 'bg-green-200' : 'bg-red-200'}`}>
                {stats.totalProfit >= 0 ? (
                  <TrendingUp size={18} className="text-green-700" />
                ) : (
                  <TrendingDown size={18} className="text-red-700" />
                )}
              </div>
              <div>
                <p className={`text-xs uppercase font-semibold ${stats.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  Beneficio Total
                </p>
                <p className={`text-2xl font-bold font-['Barlow_Condensed'] ${stats.totalProfit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatCurrency(stats.totalProfit)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      {monthlyData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sales trend chart */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-xl font-['Barlow_Condensed']">Ventas por Mes</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="month" 
                    tickFormatter={formatMonthLabel}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === 'ventas') return [value, 'Bicis vendidas'];
                      return [formatCurrency(value), name === 'ingresos' ? 'Ingresos' : name === 'beneficio' ? 'Beneficio' : 'Alquiler'];
                    }}
                    labelFormatter={formatMonthLabel}
                  />
                  <Legend />
                  <Bar dataKey="ventas" name="Bicis vendidas" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Revenue trend chart */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-xl font-['Barlow_Condensed']">Tendencia de Ingresos</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="month" 
                    tickFormatter={formatMonthLabel}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                  <Tooltip 
                    formatter={(value, name) => {
                      const labels = { ingresos: 'Ingresos Ventas', beneficio: 'Beneficio', alquiler: 'Ingresos Alquiler' };
                      return [formatCurrency(value), labels[name] || name];
                    }}
                    labelFormatter={formatMonthLabel}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="ingresos" name="Ingresos Ventas" stroke="#3B82F6" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="alquiler" name="Ingresos Alquiler" stroke="#F97316" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="beneficio" name="Beneficio" stroke="#22C55E" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table */}
      <Card className="border-slate-200" data-testid="sold-bicycles-table">
        <CardHeader>
          <CardTitle className="text-xl font-['Barlow_Condensed']">Historial de Ventas</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner w-8 h-8" />
            </div>
          ) : filteredBicycles.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <ShoppingCart size={48} className="mb-4" />
              <p>{hasFilters ? 'No hay ventas en el período seleccionado' : 'No hay bicicletas vendidas'}</p>
              <p className="text-sm">{hasFilters ? 'Prueba con otras fechas' : 'Las ventas aparecerán aquí'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Número</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Modelo</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Talla</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">F. Venta</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">P. Compra</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Manten.</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">P. Venta</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Alquiler</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500">Beneficio</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-slate-500 text-right">Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBicycles.map((bike) => (
                    <TableRow key={bike.id} className="table-row-hover">
                      <TableCell className="font-semibold text-slate-900">{bike.number}</TableCell>
                      <TableCell className="text-slate-600">{bike.model}</TableCell>
                      <TableCell className="text-slate-600">{bike.size}</TableCell>
                      <TableCell className="text-slate-500 text-sm">{formatDate(bike.sold_date)}</TableCell>
                      <TableCell className="text-slate-600">{formatCurrency(bike.purchase_price)}</TableCell>
                      <TableCell className="text-orange-600">{formatCurrency(bike.total_maintenance_cost)}</TableCell>
                      <TableCell className="text-blue-600 font-medium">
                        {formatCurrency(bike.sold_price)}
                        {bike.payment_status === 'pendiente' && (
                          <Badge className="ml-1 bg-orange-100 text-orange-700 border-orange-300 text-[10px]" data-testid={`sold-pending-badge-${bike.id}`}>
                            Pend. {formatCurrency(bike.amount_pending)}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-600">{formatCurrency(bike.rental_income)}</TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={bike.profit >= 0 
                            ? 'bg-green-100 text-green-800 border-green-200' 
                            : 'bg-red-100 text-red-800 border-red-200'
                          }
                        >
                          {formatCurrency(bike.profit)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Link to={`/bicycles/${bike.id}`}>
                          <Button variant="outline" size="sm" className="gap-1">
                            <Eye size={14} />
                            Ver
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
